import { useEffect, useState } from "react";
import axios from "axios";
import { LogOut, Mail, Phone, RefreshCw, ShieldCheck } from "lucide-react";
import { SITE } from "../data/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "baytas_admin_token";

function formatApiErrorDetail(detail) {
  if (detail == null) return "Bir sorun oluştu. Lütfen tekrar deneyin.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default function Admin() {
  const [token, setToken] = useState(() => sessionStorage.getItem(TOKEN_KEY) || "");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [messages, setMessages] = useState(null);

  const loadMessages = async (activeToken) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/messages`, {
        headers: { Authorization: `Bearer ${activeToken}` },
      });
      setMessages(res.data.messages);
    } catch (e) {
      if (e.response?.status === 401) {
        sessionStorage.removeItem(TOKEN_KEY);
        setToken("");
      }
      setError(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) loadMessages(token);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/login`, { email, password });
      sessionStorage.setItem(TOKEN_KEY, res.data.access_token);
      setToken(res.data.access_token);
    } catch (e2) {
      setError(formatApiErrorDetail(e2.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    sessionStorage.removeItem(TOKEN_KEY);
    setToken("");
    setMessages(null);
    setEmail("");
    setPassword("");
  };

  if (!token) {
    return (
      <main className="flex min-h-screen items-center justify-center bg-paper px-4 pt-24 pb-16" data-testid="admin-login-page">
        <div className="card w-full max-w-md p-8">
          <span className="grid h-12 w-12 place-items-center rounded-md border border-gold/30 bg-gold/15 text-gold">
            <ShieldCheck size={24} />
          </span>
          <h1 className="mt-5 font-display text-3xl font-extrabold uppercase tracking-tight text-ink">
            Yönetim Girişi
          </h1>
          <p className="mt-2 text-sm text-soil">Mesaj paneline erişmek için yönetici bilgilerinizle giriş yapın.</p>

          <form onSubmit={submit} className="mt-6" data-testid="admin-login-form">
            <div>
              <label htmlFor="admin-email" className="label">E-posta</label>
              <input
                id="admin-email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="admin-login-email"
                className="input"
                placeholder="admin@baytastarim.com"
              />
            </div>
            <div className="mt-4">
              <label htmlFor="admin-password" className="label">Şifre</label>
              <input
                id="admin-password"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="admin-login-password"
                className="input"
                placeholder="••••••••"
              />
            </div>
            {error && (
              <p role="alert" data-testid="admin-login-error" className="mt-4 rounded-md border border-ember/30 bg-ember/10 px-4 py-3 text-sm font-semibold text-ember">
                {error}
              </p>
            )}
            <button
              type="submit"
              disabled={loading}
              data-testid="admin-login-submit"
              className="btn btn-primary mt-6 w-full disabled:cursor-not-allowed disabled:opacity-60"
            >
              {loading ? "Giriş yapılıyor..." : "Giriş Yap"}
            </button>
          </form>
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-paper pb-24 pt-28" data-testid="admin-messages-page">
      <div className="mx-auto max-w-5xl px-4 sm:px-8">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <span className="eyebrow">Yönetim Paneli</span>
            <h1 className="mt-2 font-display text-3xl font-extrabold uppercase tracking-tight text-ink sm:text-4xl">
              İletişim Mesajları
              {messages && (
                <span className="ml-3 align-middle font-mono text-sm tracking-widest text-soil">
                  ({messages.length})
                </span>
              )}
            </h1>
          </div>
          <div className="flex gap-3">
            <button
              type="button"
              onClick={() => loadMessages(token)}
              data-testid="admin-refresh"
              className="btn btn-ghost px-4 py-2.5"
            >
              <RefreshCw size={15} className={loading ? "animate-spin" : ""} />
              Yenile
            </button>
            <button
              type="button"
              onClick={logout}
              data-testid="admin-logout"
              className="btn btn-dark px-4 py-2.5"
            >
              <LogOut size={15} />
              Çıkış
            </button>
          </div>
        </div>

        {error && (
          <p role="alert" data-testid="admin-messages-error" className="mt-6 rounded-md border border-ember/30 bg-ember/10 px-4 py-3 text-sm font-semibold text-ember">
            {error}
          </p>
        )}

        {messages === null && !error && (
          <p className="mt-12 font-mono text-sm uppercase tracking-widest text-soil" data-testid="admin-loading">
            Yükleniyor...
          </p>
        )}

        {messages !== null && messages.length === 0 && (
          <div className="card mt-10 p-10 text-center" data-testid="admin-empty">
            <p className="font-display text-xl font-bold uppercase tracking-tight text-ink">Henüz mesaj yok</p>
            <p className="mt-2 text-sm text-soil">
              İletişim formundan gönderilen mesajlar burada listelenir.
            </p>
          </div>
        )}

        {messages && messages.length > 0 && (
          <ul className="mt-10 space-y-4" data-testid="admin-messages-list">
            {messages.map((message, i) => (
              <li key={message.id} className="card p-6" data-testid={`admin-message-item-${i}`}>
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <p className="font-display text-xl font-bold uppercase tracking-tight text-ink">
                      {message.name}
                    </p>
                    <p className="mt-1 flex flex-wrap items-center gap-4 text-sm text-soil">
                      <a href={`tel:${message.phone}`} className="inline-flex items-center gap-1.5 hover:text-ink" data-testid={`admin-message-phone-${i}`}>
                        <Phone size={14} className="text-gold" />
                        {message.phone}
                      </a>
                      <a href={`mailto:${message.email}`} className="inline-flex items-center gap-1.5 hover:text-ink" data-testid={`admin-message-email-${i}`}>
                        <Mail size={14} className="text-gold" />
                        {message.email}
                      </a>
                    </p>
                  </div>
                  <span className="font-mono text-[11px] uppercase tracking-[0.15em] text-soil">
                    {new Date(message.created_at).toLocaleString("tr-TR")}
                  </span>
                </div>
                <p className="mt-4 border-t border-line pt-4 text-sm leading-relaxed text-soil">
                  {message.message}
                </p>
              </li>
            ))}
          </ul>
        )}
      </div>
    </main>
  );
}